/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/icon/icon.js';

/**
 * Circular icon button. Fires `activate` on press rather than on release when
 * `activate-on-press` is set, so users tapping without looking do not mistake
 * the press for a counter change if their finger slides off before release.
 */
@customElement('yl-circle-button')
export class CircleButton extends LitElement {
  static styles = css`
    :host {
      --size: 40px;
      --icon-size: calc(var(--size) * 0.4);
      --background: var(--yl-circle-background);
      --color: var(--yl-circle-color);
      display: inline-block;
      flex: none;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--size);
      height: var(--size);
      padding: 0;
      border: none;
      border-radius: 50%;
      background: var(--background);
      color: var(--color);
      font-size: var(--icon-size);
      cursor: pointer;
      touch-action: manipulation;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      transition: transform 80ms ease, filter 120ms ease;
    }

    button:hover {
      filter: brightness(0.94);
    }

    :host([primary]) button:hover {
      filter: brightness(1.3);
    }

    button:active {
      transform: scale(0.94);
    }

    button:focus-visible {
      outline: 3px solid var(--yl-focus-ring);
      outline-offset: 2px;
    }

    :host([primary]) {
      --background: var(--yl-primary-background);
      --color: var(--yl-primary-color);
    }

    :host([size='small']) {
      --size: 40px;
    }

    :host([size='medium']) {
      --size: 56px;
    }

    :host([size='large']) {
      --size: 84px;
    }
  `;

  @property({ attribute: 'icon' })
  icon: string = '';

  @property({ attribute: 'label' })
  label: string = '';

  @property({ attribute: 'size', reflect: true })
  size: 'small' | 'medium' | 'large' = 'small';

  @property({ attribute: 'primary', type: Boolean, reflect: true })
  primary: boolean = false;

  @property({ attribute: 'activate-on-press', type: Boolean })
  activateOnPress: boolean = false;

  private _onPointerDown(e: PointerEvent) {
    if (!this.activateOnPress || e.button !== 0) return;
    this._activate();
  }

  private _onClick(e: MouseEvent) {
    e.stopPropagation();
    // Pointer presses were already handled on pointerdown; detail is 0 for
    // keyboard activation.
    if (this.activateOnPress && e.detail !== 0) return;
    this._activate();
  }

  private _activate() {
    this.dispatchEvent(new CustomEvent('activate', { bubbles: true, composed: true }));
  }

  render() {
    return html`<button
      type="button"
      aria-label=${this.label}
      title=${this.label}
      @pointerdown=${this._onPointerDown}
      @click=${this._onClick}
    >
      <wa-icon name=${this.icon}></wa-icon>
    </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-circle-button': CircleButton;
  }
}
